import { useState, useCallback, useMemo } from 'react';

/**
 * @typedef {Object} ModalState
 * @property {string} modalId - The ID of the currently open modal
 * @property {boolean} isOpen - Whether the modal is open
 * @property {Object} data - Modal data payload
 * @property {boolean} loading - Whether the modal is in loading state
 * @property {Object} errors - Modal validation errors
 * @property {Object} config - Modal configuration
 */

/**
 * @typedef {Object} ModalActions
 * @property {function} openModal - Function to open a modal
 * @property {function} closeModal - Function to close the current modal
 * @property {function} updateModal - Function to update modal state
 * @property {function} setModalLoading - Function to set modal loading state
 * @property {function} setModalError - Function to set modal errors
 * @property {function} clearModalError - Function to clear modal errors
 * @property {function} isModalOpen - Function to check if a specific modal is open
 * @property {function} getModalData - Function to get modal data
 * @property {function} getCurrentModal - Function to get current modal state
 */

/**
 * Custom hook for managing single modal state
 * Provides a clean API for opening, closing, and updating one modal at a time
 *
 * @returns {ModalActions & { currentModal: ModalState | null }} Modal management functions and state
 *
 * @example
 * const { openModal, closeModal, isModalOpen, currentModal } = useModal();
 *
 * // Open a modal with data
 * openModal('SHARE_SUITE', { testSuite: suite });
 *
 * // Check if a specific modal is open
 * if (isModalOpen('SHARE_SUITE')) {
 *   // Modal is open
 * }
 *
 * // Close current modal
 * closeModal();
 */
const useModal = () => {
  const [currentModal, setCurrentModal] = useState(null);

  /**
   * Opens a modal with the specified ID and data
   * Automatically closes any currently open modal
   * @param {string} modalId - Unique identifier for the modal
   * @param {Object} [data={}] - Data to pass to the modal
   * @param {Object} [config={}] - Modal configuration options
   */
  const openModal = useCallback((modalId, data = {}, config = {}) => {
    setCurrentModal({
      modalId,
      isOpen: true,
      data,
      loading: false,
      errors: {},
      config,
      openedAt: Date.now(),
    });
  }, []);

  /**
   * Closes the currently open modal
   * @param {string} [modalId] - Optional modal ID for consistency with old API
   */
  const closeModal = useCallback((modalId) => {
    // Close current modal regardless of modalId parameter
    // This maintains API compatibility while simplifying to single modal
    setCurrentModal(null);
  }, []);

  /**
   * Updates current modal state with partial data
   * @param {string} modalId - Modal ID (for API compatibility, but updates current modal)
   * @param {Partial<ModalState>} updates - Partial state updates
   */
  const updateModal = useCallback((modalId, updates) => {
    setCurrentModal((prev) => {
      if (!prev || prev.modalId !== modalId) {
        // Modal doesn't match current modal, ignore update
        return prev;
      }
      return { ...prev, ...updates };
    });
  }, []);

  /**
   * Sets loading state for the current modal
   * @param {string} modalId - Modal ID (for API compatibility)
   * @param {boolean} loading - Loading state
   */
  const setModalLoading = useCallback(
    (modalId, loading) => {
      updateModal(modalId, { loading });
    },
    [updateModal]
  );

  /**
   * Sets error state for the current modal
   * @param {string} modalId - Modal ID (for API compatibility)
   * @param {Object|string} error - Error object or message
   */
  const setModalError = useCallback(
    (modalId, error) => {
      const errors = typeof error === 'string' ? { general: error } : error;
      updateModal(modalId, { errors, loading: false });
    },
    [updateModal]
  );

  /**
   * Clears all errors for the current modal
   * @param {string} modalId - Modal ID (for API compatibility)
   */
  const clearModalError = useCallback(
    (modalId) => {
      updateModal(modalId, { errors: {} });
    },
    [updateModal]
  );

  /**
   * Checks if a specific modal is currently open
   * @param {string} modalId - Unique identifier for the modal
   * @returns {boolean} Whether the specified modal is open
   */
  const isModalOpen = useCallback(
    (modalId) => {
      return currentModal?.modalId === modalId && currentModal?.isOpen === true;
    },
    [currentModal]
  );

  /**
   * Gets the current modal data
   * @param {string} [modalId] - Optional modal ID for API compatibility
   * @returns {Object|null} Modal data or null if no modal is open
   */
  const getModalData = useCallback(
    (modalId) => {
      if (modalId && currentModal?.modalId !== modalId) {
        return null;
      }
      return currentModal?.data || null;
    },
    [currentModal]
  );

  /**
   * Gets the current modal state
   * @param {string} [modalId] - Optional modal ID for API compatibility
   * @returns {ModalState|null} Modal state or null if no modal is open
   */
  const getModalState = useCallback(
    (modalId) => {
      if (modalId && currentModal?.modalId !== modalId) {
        return null;
      }
      return currentModal;
    },
    [currentModal]
  );

  /**
   * Gets the current modal (new method for single modal system)
   * @returns {ModalState|null} Current modal state or null
   */
  const getCurrentModal = useCallback(() => {
    return currentModal;
  }, [currentModal]);

  /**
   * Closes the current modal (alias for closeModal)
   */
  const closeCurrentModal = useCallback(() => {
    setCurrentModal(null);
  }, []);

  // Memoize the return object to prevent unnecessary re-renders
  const modalActions = useMemo(
    () => ({
      currentModal,
      openModal,
      closeModal,
      updateModal,
      setModalLoading,
      setModalError,
      clearModalError,
      isModalOpen,
      getModalData,
      getModalState,
      getCurrentModal,
      closeCurrentModal,
    }),
    [
      currentModal,
      openModal,
      closeModal,
      updateModal,
      setModalLoading,
      setModalError,
      clearModalError,
      isModalOpen,
      getModalData,
      getModalState,
      getCurrentModal,
      closeCurrentModal,
    ]
  );

  return modalActions;
};

export default useModal;
